import { Lock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useServices } from '../app/services';
import type { BudgetSpace } from '../domain/model';
import { useSync } from '../sync/SyncProvider';
import { usePremium } from './PremiumProvider';
import { ButtonLink } from '../ui';
import './premium.css';

/** Whether this budget is read-only now: shared, and its owner's Premium ended (sync/readOnly.ts). */
export function useReadOnly(spaceId: string | undefined | null): boolean {
  const { readOnly } = useSync();
  return Boolean(spaceId && readOnly.has(spaceId));
}

/**
 * Said wherever the budget would be written: it is read-only, why, that
 * nothing is lost, and (to its owner) how to end it. Nothing when the
 * budget is writable.
 */
export function ReadOnlyNotice({ space }: { space: Pick<BudgetSpace, 'id' | 'name' | 'ownerUserId'> }) {
  const { t } = useTranslation();
  const { identity } = useServices();
  const readOnly = useReadOnly(space.id);
  if (!readOnly) return null;
  const owner = space.ownerUserId === identity.session.userId;
  return (
    <div className="premium-readonly" role="status" data-testid="read-only">
      <Lock size={18} aria-hidden className="premium-readonly-icon" />
      <div className="stack stack-tight">
        <p className="premium-readonly-text">{t(owner ? 'premium.readOnly.owner' : 'premium.readOnly.member', { name: space.name })}</p>
        {owner && (
          <ButtonLink to="/premium?for=household" variant="primary" size="md">
            {t('premium.readOnly.renew')}
          </ButtonLink>
        )}
      </div>
    </div>
  );
}

/** The notice for the budget on screen, above everything that would change it (bills, categories). */
export function ActiveBudgetReadOnly() {
  const { activeSpace } = useServices();
  return activeSpace ? <ReadOnlyNotice space={activeSpace} /> : null;
}

/**
 * Premium ended on this device and your own budgets have changes the cloud
 * does not have: said on Home (nothing is lost; they go up on renewal).
 * Gone as soon as Premium is back or nothing waits.
 */
export function SyncPausedNotice() {
  const { t } = useTranslation();
  const premium = usePremium();
  const { account, held } = useSync();
  if (!premium.lapsed || !account || held === 0) return null;
  return (
    <div className="premium-readonly" role="status" data-testid="sync-paused">
      <Lock size={18} aria-hidden className="premium-readonly-icon" />
      <div className="stack stack-tight">
        <p className="premium-readonly-text">{t('premium.syncPaused', { count: held })}</p>
        <ButtonLink to="/premium?for=sync" variant="primary" size="md">
          {t('premium.readOnly.renew')}
        </ButtonLink>
      </div>
    </div>
  );
}
